
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import NextHead from 'next/head';
import getT from 'next-translate/getT';

// Hooks
import useIsomorphicLayoutEffect from 'hooks/use-isomorphic-layout-effect';

// Contexts
import { useToastContext } from 'context/toast-context';

// Utils
import AVAILABLE_LOCALES from 'utils/locales';
import bemify from 'utils/bemify';
import convertStringToKebabCase from 'utils/convert-string-to-kebab-case';
import getCurrencySymbol from 'utils/get-currency-symbol';
import { isOrderingPaused } from 'utils/orders-settings';
import { buildNormalizedRestaurantAddresses } from 'utils/restaurant-addresses-helpers';
import PRODUCT_TYPES from 'utils/product-types';
import { emptyNewSchedule, weekdaysByNumber } from 'utils/schedule-helper-variables';
import sanitizeRestaurantThemeColor from 'utils/sanitize-color';
import { supabase } from 'utils/supabase-client';

// Components
import Badge from 'components/badge';
import Button from 'components/button';
import Head from 'components/head';
import Header from 'components/header';
import HorizontalMenu from 'components/horizontal-menu';
import SearchInput from 'components/search-input';

// Hooks
import useRestaurantThemeColor from 'hooks/use-restaurant-theme-color';

// Restaurant Menu components
import ProductsListing from 'components/restaurant-menu/products-listing';
import RestaurantMenuInfoModal from 'components/restaurant-menu/restaurant-info-modal';
import RestaurantMenuCartSidebar from 'components/restaurant-menu/sidebar/cart';
import RestaurantMenuFiltersSidebar from 'components/restaurant-menu/sidebar/filters';
import RestaurantMenuOrderConfirmation from 'components/restaurant-menu/order-confirmation';

// Styles
import stylesModule from 'styles/pages/restaurant-menu.module.scss';


const styles = bemify(stylesModule);

const COUNTRIES_WITH_VAT_DISCLAIMER = ['PRT'];

const SIDEBAR_MENU_OPTIONS = {
  CART: 'cart',
  FILTERS: 'filters'
};

const _getServerSideProps = async (context) => {
  const {
    params,
    req,
    resolvedUrl
  } = context || {};

  const availableLocales = Object.keys(AVAILABLE_LOCALES);
  const translationGettersPromises = [];
  const translationGetters = {};

  for (const localeKey of availableLocales) {
    let commonTranslations = null;

    const translationsGetterPromise = getT(localeKey, 'common')
      .then((common) => {
        commonTranslations = common;

        return getT(localeKey, 'restaurant-menu');
      })
      .then((restaurantMenu) => {
        translationGetters[localeKey] = {
          common: commonTranslations,
          restaurantMenu
        };
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error getting translations', error);
      });

    translationGettersPromises.push(translationsGetterPromise);
  }

  await Promise.all(translationGettersPromises);

  const {
    slug: restaurantSlug
  } = params || {};

  try {
    /* eslint-disable camelcase */
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_restaurant_public_menu', {
      p_slug: restaurantSlug
    });
    /* eslint-enable camelcase */

    if (rpcError || !rpcData) {
      throw rpcError || new Error('No data returned');
    }

    const formattedData = rpcData.data;

    const {
      menu: menuRawDataRaw,
      restaurant: restaurantRawData
    } = formattedData || {};

    const menuRawData = menuRawDataRaw ?
      {
        ...menuRawDataRaw,
        publicMenuSections: menuRawDataRaw.publicMenuSections ?? menuRawDataRaw.menuSections ?? []
      } :
      { publicMenuSections: [] };

    const {
      locale: restaurantLocale,
      scheduleDayBlocks,
      ...remainingData
    } = restaurantRawData || {};

    // Format schedule
    const schedule = Array.isArray(scheduleDayBlocks) && scheduleDayBlocks.length ?
      scheduleDayBlocks.reduce((newSchedule, dayBlock) => {
        const weekday = weekdaysByNumber[dayBlock.weekday];

        const timeEntry = {
          closeTime: dayBlock.endHour,
          openTime: dayBlock.startHour
        };

        const weekdayPositionInNewSchedule = newSchedule.findIndex(entry => entry.day === weekday);

        if (weekdayPositionInNewSchedule !== -1) {
          newSchedule[weekdayPositionInNewSchedule].timeEntries.push(timeEntry);
        } else {
          const newDay = {
            day: weekday,
            timeEntries: [timeEntry]
          };

          newSchedule.push(newDay);
        }

        return newSchedule;
      }, JSON.parse(JSON.stringify(emptyNewSchedule))) :
      JSON.parse(JSON.stringify(emptyNewSchedule));

    // Calculate products allergens, highest and lowest price, and available product types
    const productTypes = [PRODUCT_TYPES.ALL];

    const productsByProductId = {};
    let productsAllergens = [];

    let productsHighestPrice = null;
    let productsLowestPrice = null;

    if (Array.isArray(menuRawData?.publicMenuSections)) {
      menuRawData.publicMenuSections.forEach((section, sectionIndex) => {
        if (Array.isArray(section?.menuSectionProducts)) {
          section.menuSectionProducts.forEach((sectionProduct, sectionProductIndex) => {
            const { product } = sectionProduct || {};

            if (product) {
              if (!productsByProductId[product.entityId]) {
                const fallbackTranslations = {
                  description: '',
                  ingredients: '',
                  title: ''
                };

                const { translations: productTranslations } = product || {};
                const productTranslationLocales = Object.keys(productTranslations);

                if (productTranslationLocales.length) {
                  for (const translationLocale of productTranslationLocales) {
                    const {
                      description,
                      ingredients,
                      title
                    } = productTranslations[translationLocale] || {};

                    if (description && !fallbackTranslations.description) fallbackTranslations.description = description;
                    if (ingredients && !fallbackTranslations.ingredients) fallbackTranslations.ingredients = ingredients;
                    if (title && !fallbackTranslations.title) fallbackTranslations.title = title;

                    if (
                      fallbackTranslations.description &&
                      fallbackTranslations.ingredients &&
                      fallbackTranslations.title
                    ) {
                      break;
                    }
                  }
                }

                const formattedProduct = {
                  ...product,
                  fallbackTranslations
                };

                productsByProductId[product.entityId] = formattedProduct;
                menuRawData.publicMenuSections[sectionIndex].menuSectionProducts[sectionProductIndex].product = formattedProduct;
              }

              if (!productsHighestPrice || product.price > productsHighestPrice) productsHighestPrice = product.price;
              if (!productsLowestPrice || product.price < productsLowestPrice) productsLowestPrice = product.price;

              if (Array.isArray(product.allergens) && product.allergens?.length && product.allergens.some(allergen => !productsAllergens.includes(allergen))) {
                const combinedAllergens = new Set([...productsAllergens, ...product.allergens]);

                productsAllergens = Array.from(combinedAllergens);
              }

              const { isBeverage } = product;

              if (productTypes.length < 3) {
                if (isBeverage && !productTypes.includes(PRODUCT_TYPES.BEVERAGE)) productTypes.push(PRODUCT_TYPES.BEVERAGE);
                else if (!isBeverage && !productTypes.includes(PRODUCT_TYPES.DISH)) productTypes.push(PRODUCT_TYPES.DISH);
              }
            }
          });
        }
      });
    }

    // Always use restaurant's locale from database, fallback to first available locale if not set
    const locale = (restaurantLocale && availableLocales.includes(restaurantLocale)) ?
      restaurantLocale :
      availableLocales[0];

    const restaurantData = {
      locale,
      ...remainingData,
      menu: {
        ...menuRawData,
        productTypes,
        productsAllergens,
        productsByProductId,
        productsHighestPrice,
        productsLowestPrice
      },
      schedule
    };

    // We need to get translations on the server-side in order to access them in the page props
    const pageTranslationsByLocale = Object.entries(translationGetters).reduce((accumulator, [localeKey, translationFunctions]) => {
      const {
        common,
        restaurantMenu
      } = translationFunctions;

      const localeOptions = Object.keys(AVAILABLE_LOCALES).reduce((localeOptionsByLocaleKey, optionLocaleKey) => {
        localeOptionsByLocaleKey[optionLocaleKey] = common(AVAILABLE_LOCALES[optionLocaleKey]?.labelTranslation);

        return localeOptionsByLocaleKey;
      }, {});

      accumulator[localeKey] = {
        actions: {
          addToCart: common('actions.add_to_cart'),
          orderRegistrationError: restaurantMenu('actions.order_registration_error'),
          orderRegistrationErrorPickupDatetimeTooFar: restaurantMenu('actions.order_registration_error_pickup_datetime_too_far'),
          orderRegistrationErrorPickupDatetimeTooSoon: restaurantMenu('actions.order_registration_error_pickup_datetime_too_soon'),
          orderRegistrationParametersMissing: restaurantMenu('actions.order_registration_parameters_missing'),
          productAddedToCart: restaurantMenu('actions.product_added_to_cart')
        },
        cart: {
          checkoutButtonLabel: restaurantMenu('sidebar_cart.checkout_button'),
          noProducts: restaurantMenu('sidebar_cart.no_products'),
          returnButtonLabel: restaurantMenu('sidebar_cart.return_button'),
          title: restaurantMenu('sidebar_cart.title'),
          totalLabel: restaurantMenu('sidebar_cart.total_label')
        },
        filters: {
          allergens: {
            // We need to use the kebab-case version of the allergen to access the correct label translation
            'gluten-free-diet': common('allergens.gluten_free'),
            kosher: common('allergens.kosher'),
            'lactose-intolerance': common('allergens.lactose_intolerance'),
            'low-calories': common('allergens.low_calories'),
            sugar: common('allergens.sugar'),
            'tree-nuts': common('allergens.tree_nuts'),
            'vegan-diet': common('allergens.vegan'),
            'vegetarian-diet': common('allergens.vegetarian')
          },
          cart: {
            checkoutButtonLabel: restaurantMenu('sidebar_cart.checkout_button'),
            noProducts: restaurantMenu('sidebar_cart.no_products'),
            returnButtonLabel: restaurantMenu('sidebar_cart.return_button'),
            title: restaurantMenu('sidebar_cart.title'),
            totalLabel: restaurantMenu('sidebar_cart.total_label')
          },
          dietFilterSectionTitle: restaurantMenu('sidebar_filters.diet_tags.title'),
          priceIntervalFilterSectionTitle: restaurantMenu('sidebar_filters.price_interval.title', { currency: getCurrencySymbol(restaurantData.currency) }),
          productTypeFilterSectionTitle: restaurantMenu('sidebar_filters.product_type.title'),
          rangeMaximumPriceLabel: restaurantMenu('sidebar_filters.price_interval.maximum_price.label'),
          rangeMaximumPricePlaceholder: restaurantMenu('sidebar_filters.price_interval.maximum_price.placeholder'),
          rangeMinimumPriceLabel: restaurantMenu('sidebar_filters.price_interval.minimum_price.label'),
          rangeMinimumPricePlaceholder: restaurantMenu('sidebar_filters.price_interval.minimum_price.placeholder'),
          resetFilterButtonLabel: restaurantMenu('sidebar_filters.reset'),
          returnButtonLabel: restaurantMenu('sidebar_filters.return_button'),
          typeFilterAllLabel: restaurantMenu('sidebar_filters.product_type.types.all'),
          typeFilterBeveragesLabel: restaurantMenu('sidebar_filters.product_type.types.beverages'),
          typeFilterDishesLabel: restaurantMenu('sidebar_filters.product_type.types.dishes')
        },
        footnote: restaurantMenu('footnote'),
        holidayTranslation: common('holiday'),
        localeOptions,
        orderConfirmation: {
          confirmButtonLabel: restaurantMenu('order_confirmation.confirm_button'),
          fields: {
            email: {
              label: restaurantMenu('order_confirmation.fields.email.label'),
              placeholder: restaurantMenu('order_confirmation.fields.email.placeholder'),
              validation: {
                pattern: restaurantMenu('order_confirmation.fields.email.validation.pattern'),
                required: restaurantMenu('order_confirmation.fields.email.validation.required')
              }
            },
            name: {
              label: restaurantMenu('order_confirmation.fields.name.label'),
              placeholder: restaurantMenu('order_confirmation.fields.name.placeholder'),
              validation: {
                required: restaurantMenu('order_confirmation.fields.name.validation.required')
              }
            },
            notes: {
              label: restaurantMenu('order_confirmation.fields.notes.label'),
              placeholder: restaurantMenu('order_confirmation.fields.notes.placeholder'),
              validation: {
                invalid: restaurantMenu('order_confirmation.fields.notes.validation.invalid'),
                maxLength: restaurantMenu('order_confirmation.fields.notes.validation.maxLength')
              }
            },
            phone: {
              label: restaurantMenu('order_confirmation.fields.phone.label'),
              placeholder: restaurantMenu('order_confirmation.fields.phone.placeholder'),
              validation: {
                pattern: restaurantMenu('order_confirmation.fields.phone.validation.pattern'),
                required: restaurantMenu('order_confirmation.fields.phone.validation.required')
              }
            },
            pickupAddress: {
              label: restaurantMenu('order_confirmation.fields.pickup_address.label'),
              placeholder: restaurantMenu('order_confirmation.fields.pickup_address.placeholder'),
              singleLocationLabel: restaurantMenu('order_confirmation.fields.pickup_address.singleLocationLabel'),
              validation: {
                required: restaurantMenu('order_confirmation.fields.pickup_address.validation.required')
              }
            },
            pickupDateTime: {
              label: restaurantMenu('order_confirmation.fields.pickup_datetime.label'),
              placeholder: restaurantMenu('order_confirmation.fields.pickup_datetime.placeholder'),
              restaurantTimeNote: restaurantMenu('order_confirmation.fields.pickup_datetime.restaurant_time_note'),
              validation: {
                future: restaurantMenu('order_confirmation.fields.pickup_datetime.validation.future'),
                invalid: restaurantMenu('order_confirmation.fields.pickup_datetime.validation.invalid'),
                outsideHours: restaurantMenu('order_confirmation.fields.pickup_datetime.validation.outside_hours'),
                range: restaurantMenu('order_confirmation.fields.pickup_datetime.validation.range'),
                required: restaurantMenu('order_confirmation.fields.pickup_datetime.validation.required')
              }
            }
          },
          returnButtonLabel: restaurantMenu('order_confirmation.return_button'),
          success: {
            message: restaurantMenu('order_confirmation.success.message'),
            returnButton: restaurantMenu('order_confirmation.success.return_button'),
            title: restaurantMenu('order_confirmation.success.title'),
            trackButton: restaurantMenu('order_confirmation.success.track_button')
          },
          totalLabel: restaurantMenu('order_confirmation.total_label')
        },
        products: {
          ingredients: common('product_card_modal.ingredients')
        },
        restaurantCoverPhotoAltTranslation: common('restaurant_images_alt.cover'),
        restaurantInfoModalInformationTranslation: restaurantMenu('hero.modal.information'),
        restaurantInfoModalScheduleSeeLessTranslation: restaurantMenu('modal.schedule_see_less'),
        restaurantInfoModalScheduleSeeMoreTranslation: restaurantMenu('modal.schedule_see_more'),
        restaurantInfoModalSocialMediaTranslation: restaurantMenu('hero.modal.social_media'),
        restaurantInfoTranslation: restaurantMenu('hero.more_info'),
        restaurantLogoAltTranslation: common('restaurant_images_alt.logo'),
        restaurantScheduleClosedTranslation: restaurantMenu('hero.schedule_text.closed'),
        restaurantScheduleOpenTranslation: restaurantMenu('hero.schedule_text.open'),
        search: {
          inputPlaceholder: common('search_input_placeholder'),
          noResults: {
            description: common('search_no_results.description'),
            title: common('search_no_results.title')
          }
        },
        weekdays: {
          friday: common('weekdays.friday'),
          monday: common('weekdays.monday'),
          saturday: common('weekdays.saturday'),
          sunday: common('weekdays.sunday'),
          thursday: common('weekdays.thursday'),
          tuesday: common('weekdays.tuesday'),
          wednesday: common('weekdays.wednesday')
        }
      };

      return accumulator;
    }, {});

    const hostHeader = req?.headers?.['x-forwarded-host'] || req?.headers?.host || '';
    const protocolHeader = req?.headers?.['x-forwarded-proto'];

    let protocol = 'https';

    if (protocolHeader) {
      protocol = protocolHeader.split(',')[0];
    } else if (hostHeader?.includes('localhost')) {
      protocol = 'http';
    }

    const resolvedPath = resolvedUrl ? resolvedUrl.split('?')[0] : '';
    const fallbackBase = process.env.NEXT_PUBLIC_WEBSITE_URL || process.env.NEXT_PUBLIC_BASE_URL || '';
    const canonicalBase = hostHeader ? `${protocol}://${hostHeader}` : fallbackBase;
    const normalizedBase = canonicalBase?.endsWith('/') ? canonicalBase.slice(0, -1) : canonicalBase;
    const canonicalUrl = normalizedBase ?
      `${normalizedBase}${resolvedPath.startsWith('/') ? '' : '/'}${resolvedPath}` :
      resolvedPath;

    return {
      props: {
        canonicalUrl,
        pageTranslationsByLocale,
        restaurantData
      }
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting restaurant data', error);

    return {
      notFound: true
    };
  }
};

const RestaurantMenu = ({
  canonicalUrl: canonicalUrlFromServer,
  pageTranslationsByLocale,
  restaurantData = null
}) => {
  const router = useRouter();
  const { showToast } = useToastContext();

  // States
  const [cartProductsQuantityByProductId, setCartProductsQuantityByProductId] = useState({});
  const [confirmedOrderId, setConfirmedOrderId] = useState(null);
  const [confirmedOrderProducts, setConfirmedOrderProducts] = useState([]);
  const [filtersAppliedAmount, setFiltersAppliedAmount] = useState(0);
  const [filtersAreVisible, setFiltersAreVisible] = useState(false);
  const [cartIsVisible, setCartIsVisible] = useState(false);
  const [filteredMenuSections, setFilteredMenuSections] = useState(null);
  const [isRegisteringOrder, setIsRegisteringOrder] = useState(false);
  const [lastActiveSidebarMenu, setLastActiveSidebarMenu] = useState(SIDEBAR_MENU_OPTIONS.FILTERS);
  const [menuSectionsMenuAndSearchIsSticky, setMenuSectionsMenuAndSearchIsSticky] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [reduceProductsGrid, setReduceProductsGrid] = useState(false);
  const [restaurantIsOpen, setRestaurantIsOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedMenuSectionId, setSelectedMenuSectionId] = useState(null);
  const [selectedAllergens, setSelectedAllergens] = useState(null);
  const [selectedLocale, setSelectedLocale] = useState(restaurantData.locale);
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [showOrderConfirmationModal, setShowOrderConfirmationModal] = useState(false);
  const [weekSchedule, setWeekSchedule] = useState(null);

  // Refs
  const cartCheckoutProductsByProductId = useRef(null);
  const menuSectionsAndSearch = useRef(null);
  const observer = useRef(null);
  const reduceProductGridTimeout = useRef(null);
  const scrolledAutomatically = useRef(false);
  const sectionWasManuallySelected = useRef(false);
  const waitingForScroll = useRef(null);

  const {
    menu,
    ordersClosingOffsetMinutes = 0,
    ordersEnabled = true,
    ordersPausedUntil = null,
    schedule,
    scheduleClosedEvents
  } = restaurantData;

  // When ordering is disabled (persistent setting) or temporarily paused, the
  // public site becomes a read-only menu: no add-to-cart controls, no cart
  // sidebar and no checkout.
  const orderingIsEnabled = ordersEnabled !== false && !isOrderingPaused(ordersPausedUntil);
  const rawRestaurantThemeColor = typeof restaurantData?.color === 'string' ?
    restaurantData.color.trim() :
    '';

  // Sanitize theme color before inlining or using in hooks to avoid CSS injection.
  const restaurantThemeColor = sanitizeRestaurantThemeColor(rawRestaurantThemeColor);

  useRestaurantThemeColor(restaurantThemeColor);

  // Computed
  const cartProducts = useMemo(
    () => Object.keys(cartProductsQuantityByProductId).map(productId => ({
      allergens: menu.productsByProductId[productId]?.allergens,
      entityId: productId,
      fallbackTranslations: menu.productsByProductId[productId]?.fallbackTranslations,
      imageUrl: menu.productsByProductId[productId]?.imageUrl,
      price: menu.productsByProductId[productId]?.price,
      quantity: cartProductsQuantityByProductId[productId],
      translations: menu.productsByProductId[productId]?.translations
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartProductsQuantityByProductId]
  );

  const cartProductsQuantity = useMemo(
    () => Object.values(cartProductsQuantityByProductId).reduce((acc, quantity) => acc + quantity, 0),
    [cartProductsQuantityByProductId]
  );

  const menuSections = useMemo(
    () => {
      if (Array.isArray(menu?.publicMenuSections)) {
        return menu.publicMenuSections
          .map((menuSection) => {
            const menuSectionTranslations = menuSection.translations?.[selectedLocale] ||
              menuSection.translations?.[Object.keys(menuSection.translations).find(key => !!menuSection.translations[key].name)];

            return {
              active: filteredMenuSections?.some(filteredMenuSection => filteredMenuSection.id === menuSection.id),
              id: menuSection.id,
              name: menuSectionTranslations.name || menuSection.name,
              slug: convertStringToKebabCase(menuSectionTranslations.name)
            };
          });
      }

      return [];
    },
    [menu, filteredMenuSections, selectedLocale]
  );

  const closedDays = useMemo(() => {
    const newClosedDays = [];

    if (Array.isArray(scheduleClosedEvents)) {
      scheduleClosedEvents.forEach((event) => {
        const {
          startDay,
          endDay
        } = event.startAndEndDays || {};

        const startDate = new Date(startDay);
        const endDate = new Date(endDay);

        const currentDate = startDate;

        while (currentDate <= endDate) {
          newClosedDays.push({
            date: new Date(currentDate),
            eventName: event.name
          });

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });
    }

    return newClosedDays;
  }, [scheduleClosedEvents]);

  const pageTranslations = pageTranslationsByLocale[selectedLocale];

  const currentDate = new Date();
  const currentDateWeekDay = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(currentDate);

  // Methods
  const handleCartCheckout = () => {
    cartCheckoutProductsByProductId.current = {
      ...cartProductsQuantityByProductId
    };

    setShowOrderConfirmationModal(true);
  };

  const handleCartProductUpdate = (productId, quantity) => {
    setCartProductsQuantityByProductId((previousValue) => {
      if (quantity === 0) {
        const newCartProductsByProductId = {
          ...previousValue
        };

        delete newCartProductsByProductId[productId];

        return newCartProductsByProductId;
      }

      return {
        ...previousValue,
        [productId]: quantity
      };
    });
  };

  const handleFilteredMenuSectionsUpdate = (newFilteredSections) => {
    setFilteredMenuSections(newFilteredSections);
  };

  const handleMoreInfoButtonClick = () => {
    setShowMoreInfoModal(true);
  };

  const handleMoreInfoModalClose = () => {
    setShowMoreInfoModal(false);
  };

  const toggleCart = () => {
    setCartIsVisible(previousValue => !previousValue);
  };

  const toggleFilters = () => {
    setFiltersAreVisible(previousValue => !previousValue);
  };

  const handleDocumentScroll = () => {
    if (sectionWasManuallySelected.current && waitingForScroll.current && !scrolledAutomatically.current) {
      scrolledAutomatically.current = true;
    }
  };

  const handleDocumentScrollEnd = () => {
    if (!waitingForScroll.current) {
      sectionWasManuallySelected.current = false;
      scrolledAutomatically.current = false;
    }
  };

  const handleCurrentScrollSectionIdChange = (sectionId) => {
    if (!sectionWasManuallySelected.current) {
      setSelectedMenuSectionId(sectionId);

      window.history.replaceState(null, null, `#${sectionId}`);
    }
  };

  const handleAllergensFilterUpdate = (newValue) => {
    const selectedAllergensIds = newValue.filter(allergen => allergen.checked).map(allergen => allergen.id);

    setSelectedAllergens(selectedAllergensIds);
  };

  const normalizedRestaurantAddresses = useMemo(
    () => buildNormalizedRestaurantAddresses(
      restaurantData?.address,
      restaurantData?.addresses
    ),
    [restaurantData.address, restaurantData.addresses]
  );

  const defaultRestaurantAddress = normalizedRestaurantAddresses[0] || null;

  const handleOrderConfirmation = async (data) => {
    setIsRegisteringOrder(true);

    const {
      email: customerEmail,
      name: customerName,
      phone: customerPhone,
      notes,
      pickupDateTime,
      products,
      resetForm,
      restaurantTimezone,
      restaurantAddress
    } = data || {};

    if (
      !customerEmail ||
      !customerName ||
      !customerPhone ||
      !pickupDateTime ||
      !Array.isArray(products) ||
      !products.length
    ) {
      showToast(pageTranslations.actions.orderRegistrationParametersMissing, 'error');
    } else {
      const pickupDate = pickupDateTime instanceof Date ? pickupDateTime : new Date(pickupDateTime);

      if (Number.isNaN(pickupDate.getTime()) || pickupDate.getTime() <= Date.now()) {
        showToast(pageTranslations.actions.orderRegistrationParametersMissing, 'error');
        setIsRegisteringOrder(false);

        return;
      }

      const formattedProducts = products.map(product => ({
        id: product.entityId,
        quantity: product.quantity
      }));

      const customerDetails = {
        email: customerEmail,
        name: customerName,
        phone: customerPhone
      };

      const sanitizedNotes = typeof notes === 'string' ? notes.trim() : '';

      if (sanitizedNotes.length > 500) {
        showToast(pageTranslations.actions.orderRegistrationParametersMissing, 'error');
        setIsRegisteringOrder(false);

        return;
      }

      const selectedRestaurantAddress = restaurantAddress || defaultRestaurantAddress;

      const restaurantAddressPayload = selectedRestaurantAddress ?
        {
          address: selectedRestaurantAddress.address,
          id: selectedRestaurantAddress.id,
          label: selectedRestaurantAddress.label
        } :
        null;

      const order = {
        customerDetails,
        notes: sanitizedNotes || null,
        orderItems: formattedProducts,
        pickupDateTime: pickupDate.toISOString(),
        restaurantAddress: restaurantAddressPayload,
        restaurantId: restaurantData.id,
        restaurantTimezone
      };

      try {
        const { data: registerOrderData, error } = await supabase.functions.invoke('register-order', {
          body: {
            order
          }
        });

        if (error || registerOrderData?.error) {
          if (registerOrderData?.error && !error) {
            throw new Error(registerOrderData.error);
          }

          throw error;
        }

        setConfirmedOrderId(registerOrderData?.id || null);
        setConfirmedOrderProducts(products);
        setCartProductsQuantityByProductId({});
        setCartIsVisible(false);
        setOrderSuccess(true);

        // Reset form on success
        if (typeof resetForm === 'function') {
          resetForm();
        }
      } catch (error) {
        // Check for specific error types
        const errorMessage = error?.message || error;

        if (errorMessage === 'pickup_datetime_too_soon') {
          showToast(pageTranslations.actions.orderRegistrationErrorPickupDatetimeTooSoon, 'error');
        } else if (errorMessage === 'pickup_datetime_too_far') {
          showToast(pageTranslations.actions.orderRegistrationErrorPickupDatetimeTooFar, 'error');
        } else {
          showToast(pageTranslations.actions.orderRegistrationError, 'error');
        }
      }
    }

    setIsRegisteringOrder(false);
  };

  const handlePriceIntervalFilterUpdate = (newValue) => {
    setSelectedPriceRange(newValue);
  };

  const handleProductAddToCart = (productId, quantity) => {
    setCartProductsQuantityByProductId((previousValue) => {
      const newQuantity = previousValue[productId] ? previousValue[productId] + quantity : quantity;

      return {
        ...previousValue,
        [productId]: newQuantity
      };
    });

    if (!cartIsVisible) {
      showToast(pageTranslations.actions.productAddedToCart, 'success');
    }
  };

  const handleProductTypeFilterUpdate = (newValue) => {
    setSelectedProductType(newValue);
  };

  const handleSearchInputChange = (value) => {
    setSearchInputValue(value);
  };

  const handleSectionAnchorClick = () => {
    sectionWasManuallySelected.current = true;

    waitingForScroll.current = setTimeout(() => {
      waitingForScroll.current = null;

      // We need to check this in case there was no scroll
      // Otherwise, the active section update will be blocked until the next scroll end
      if (!scrolledAutomatically.current) {
        sectionWasManuallySelected.current = false;
      }
    }, 500);
  };

  // Effects
  useIsomorphicLayoutEffect(() => {
    document.addEventListener('scroll', handleDocumentScroll);
    document.addEventListener('scrollend', handleDocumentScrollEnd);

    if (menuSectionsAndSearch?.current) {
      observer.current = new IntersectionObserver(
        ([event]) => setMenuSectionsMenuAndSearchIsSticky(event.intersectionRatio < 1),
        { threshold: [1] }
      );

      observer.current.observe(menuSectionsAndSearch.current);
    }

    // Check if there are products in the session storage
    const storedCartProductsQuantityByProductId = sessionStorage.getItem('cartProductsQuantityByProductId');

    if (storedCartProductsQuantityByProductId) {
      try {
        const parsedCartProductsQuantityByProductId = JSON.parse(storedCartProductsQuantityByProductId);

        if (Object.keys(parsedCartProductsQuantityByProductId)?.length) {
          // Filter out products that are no longer in the menu
          const filteredCartProductsQuantityByProductId = Object.keys(parsedCartProductsQuantityByProductId)
            .reduce((accumulator, productId) => {
              if (menu.productsByProductId?.[productId]) {
                accumulator[productId] = parsedCartProductsQuantityByProductId[productId];
              }

              return accumulator;
            }, {});

          setCartProductsQuantityByProductId(filteredCartProductsQuantityByProductId);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    return () => {
      document.removeEventListener('scroll', handleDocumentScroll);
      document.removeEventListener('scrollend', handleDocumentScrollEnd);

      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const { asPath } = router || {};

    if (asPath) {
      const hash = asPath.split('#')[1];

      setSelectedMenuSectionId(hash);
    }
  }, [router]);

  useEffect(() => {
    if (Array.isArray(schedule) && schedule.length) {
      const copyOfSchedule = JSON.parse(JSON.stringify(schedule));

      // Sort the schedule so that it starts with the current day
      const todayWeekDayName = new Intl.DateTimeFormat('en-US', { weekday: 'long' })
        .format(currentDate)
        .toLowerCase();

      const currentDateWeekDayIndex = copyOfSchedule.findIndex(entry => entry.day.toLowerCase() === todayWeekDayName);

      const copyOfScheduleSorted = copyOfSchedule.slice(currentDateWeekDayIndex).concat(copyOfSchedule.slice(0, currentDateWeekDayIndex));

      const currentDateDay = currentDate.getDate();
      const currentDateMonth = currentDate.getMonth();
      const currentDateYear = currentDate.getFullYear();

      const newSchedule = copyOfScheduleSorted.map((entry, index) => {
        const dayDate = new Date(currentDateYear, currentDateMonth, currentDateDay + index);

        const closedDayOnCurrentDate = closedDays.find(closedDay => closedDay.date.getDate() === dayDate.getDate() &&
          closedDay.date.getMonth() === dayDate.getMonth() &&
          closedDay.date.getFullYear() === dayDate.getFullYear());

        if (Array.isArray(entry.timeEntries) && entry.timeEntries.length && !closedDayOnCurrentDate) {
          const { timeEntries } = entry;

          const newTimeEntries = timeEntries.map((timeEntry) => {
            const closeTimeIsInNextDay = timeEntry.closeTime < timeEntry.openTime;
            const closeTimeHourAndMinutes = timeEntry.closeTime?.split(':');
            const openTimeHourAndMinutes = timeEntry.openTime?.split(':');

            const closeTime = new Date(
              currentDateYear,
              currentDateMonth,
              currentDateDay + (closeTimeIsInNextDay ? index + 1 : index),
              closeTimeHourAndMinutes[0],
              closeTimeHourAndMinutes[1]
            );

            const openTime = new Date(
              currentDateYear,
              currentDateMonth,
              currentDateDay + index,
              openTimeHourAndMinutes[0],
              openTimeHourAndMinutes[1]
            );

            return {
              closeTime,
              openTime
            };
          });

          return {
            day: entry.day,
            timeEntries: newTimeEntries
          };
        }

        if (Array.isArray(entry.timeEntries) && entry.timeEntries.length && closedDayOnCurrentDate) {
          return {
            ...entry,
            closedEvent: closedDayOnCurrentDate.eventName || pageTranslations.holidayTranslation
          };
        }

        return entry;
      });

      setWeekSchedule(newSchedule);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  useEffect(() => {
    if (Array.isArray(weekSchedule) && weekSchedule.length) {
      weekSchedule.forEach((entry, index) => {
        if (entry.day === currentDateWeekDay) {
          if (Array.isArray(entry.timeEntries)) {
            let isOpen = entry.timeEntries.some(timeEntry => currentDate > timeEntry.openTime && currentDate < timeEntry.closeTime);

            // Check if it's open from previous day, if it's not open
            if (!isOpen) {
              const previousDay = weekSchedule[((index - 1) + weekSchedule.length) % weekSchedule.length];
              const previousDayDate = new Date(currentDate);

              previousDayDate.setDate(previousDayDate.getDate() - 1);

              isOpen = previousDay.timeEntries.some((timeEntry) => {
                const openTime = new Date(timeEntry.openTime);
                const closeTime = new Date(timeEntry.closeTime);

                openTime.setDate(openTime.getDate() - 7);
                closeTime.setDate(closeTime.getDate() - 7);

                return currentDate > openTime && currentDate < closeTime;
              });

              // if it's open through previous day, ensure that previous day is no a Holiday
              if (isOpen) {
                isOpen = !closedDays.some(closedDay => closedDay.date.getDate() === previousDayDate.getDate() &&
                  closedDay.date.getMonth() === previousDayDate.getMonth() &&
                  closedDay.date.getFullYear() === previousDayDate.getFullYear());
              }
            }

            setRestaurantIsOpen(isOpen);
          }
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekSchedule]);

  useEffect(() => {
    if ((filtersAreVisible || cartIsVisible) && window.innerWidth < 1200) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    if (reduceProductGridTimeout.current) clearTimeout(reduceProductGridTimeout.current);

    reduceProductGridTimeout.current = setTimeout(() => {
      setReduceProductsGrid(filtersAreVisible || cartIsVisible);

      reduceProductGridTimeout.current = null;
    // This value is half the duration of the expand/contract animation
    }, 200);
  }, [filtersAreVisible, cartIsVisible]);

  useEffect(() => {
    let filtersAmount = 0;

    const selectedPriceRangeIsCustom = selectedPriceRange &&
      (selectedPriceRange.minimum !== Math.floor(restaurantData.menu.productsLowestPrice) ||
      selectedPriceRange.maximum !== Math.ceil(restaurantData.menu.productsHighestPrice));

    const selectedProductTypeIsCustom = selectedProductType && selectedProductType !== PRODUCT_TYPES.ALL;

    if (selectedPriceRange && selectedPriceRangeIsCustom) filtersAmount += 1;
    if (selectedProductType && selectedProductTypeIsCustom) filtersAmount += 1;
    if (Array.isArray(selectedAllergens) && selectedAllergens.length) filtersAmount += selectedAllergens.length;

    setFiltersAppliedAmount(filtersAmount);
  }, [restaurantData, selectedAllergens, selectedPriceRange, selectedProductType]);

  useEffect(() => {
    if (Object.keys(cartProductsQuantityByProductId).length) {
      // Store the cart products in the session storage
      sessionStorage.setItem('cartProductsQuantityByProductId', JSON.stringify(cartProductsQuantityByProductId));
    } else {
      // Remove the cart products from the session storage
      sessionStorage.removeItem('cartProductsQuantityByProductId');
    }
  }, [cartProductsQuantityByProductId]);

  useEffect(() => {
    if (cartIsVisible) {
      setLastActiveSidebarMenu(SIDEBAR_MENU_OPTIONS.CART);

      setFiltersAreVisible(false);
    }
  }, [cartIsVisible]);

  useEffect(() => {
    if (filtersAreVisible) {
      setLastActiveSidebarMenu(SIDEBAR_MENU_OPTIONS.FILTERS);

      setCartIsVisible(false);
    }
  }, [filtersAreVisible]);

  useEffect(() => {
    if (showOrderConfirmationModal) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
      setOrderSuccess(false);
      setConfirmedOrderProducts([]);
    }
  }, [showOrderConfirmationModal]);

  const poweredByLink = `${
    process.env.NEXT_PUBLIC_WEBSITE_URL
  }?utm_source=${
    encodeURIComponent(`public-menu-${restaurantData.name}`)
  }&utm_medium=web&utm_campaign=powered-by`;

  // Only build the tracking URL when a base URL is configured, so the success
  // button is never rendered with a malformed href (e.g. "undefined/order/...").
  const orderTrackingBaseUrl = (
    process.env.NEXT_PUBLIC_BASE_URL || ''
  ).replace(/\/+$/, '');
  const trackOrderUrl = confirmedOrderId && orderTrackingBaseUrl ?
    `${orderTrackingBaseUrl}/order/${confirmedOrderId}` :
    null;

  // Generate dynamic favicon URL from restaurant logo
  const faviconUrl = restaurantData.logoUrl ?
    `/api/favicon?logoUrl=${encodeURIComponent(restaurantData.logoUrl)}` :
    null;

  // SEO: Construct enhanced meta description
  const metaAddressText = defaultRestaurantAddress?.address || '';

  const metaDescription = restaurantData.description && metaAddressText ?
    `${restaurantData.description} - ${restaurantData.category} restaurant located at ${metaAddressText}` :
    `${restaurantData.name} - ${restaurantData.category} restaurant menu${metaAddressText ? `. Order online from ${metaAddressText}` : ''}`;

  // SEO: Generate keywords
  const metaKeywords = [
    restaurantData.name,
    restaurantData.category,
    'restaurant',
    'menu',
    'online ordering',
    metaAddressText?.split(',')[1]?.trim(),
    restaurantData.country
  ].filter(Boolean).join(', ');

  // SEO: Canonical URL (handles both subdomain and custom domain)
  const canonicalUrl = canonicalUrlFromServer || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');

  // SEO: Generate Restaurant structured data (schema.org)
  const restaurantStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    ...(defaultRestaurantAddress && {
      address: {
        '@type': 'PostalAddress',
        addressCountry: restaurantData.country,
        streetAddress: defaultRestaurantAddress.address
      }
    }),
    description: restaurantData.description || metaDescription,
    image: restaurantData.coverPhotoUrl || restaurantData.logoUrl,
    name: restaurantData.name,
    servesCuisine: restaurantData.category,
    telephone: restaurantData.phoneNumber,
    url: canonicalUrl,
    ...(restaurantData.email && { email: restaurantData.email }),
    ...(menu.publicMenuSections && menu.publicMenuSections.length > 0 && {
      hasMenu: {
        '@type': 'Menu',
        hasMenuSection: menu.publicMenuSections.map((section) => {
          const sectionTranslations = section.translations?.[selectedLocale] ||
            section.translations?.[Object.keys(section.translations || {}).find(key => !!section.translations[key].name)];

          return {
            '@type': 'MenuSection',
            hasMenuItem: (section.menuSectionProducts ?? []).map((sectionProduct) => {
              const { product } = sectionProduct || {};

              return {
                '@type': 'MenuItem',
                description: product.translations?.[selectedLocale]?.description || product.fallbackTranslations?.description,
                name: product.translations?.[selectedLocale]?.title || product.fallbackTranslations?.title,
                offers: {
                  '@type': 'Offer',
                  price: product.price,
                  priceCurrency: restaurantData.currency
                }
              };
            }).filter(item => item.name),
            name: sectionTranslations?.name || section.name
          };
        })
      }
    }),
    ...(weekSchedule && weekSchedule.length > 0 && {
      openingHoursSpecification: weekSchedule
        .filter(day => Array.isArray(day.timeEntries) && day.timeEntries.length > 0 && !day.closedEvent)
        .map((day) => {
          const { openTime } = day.timeEntries[0];
          const { closeTime } = day.timeEntries[day.timeEntries.length - 1] || {};

          return {
            '@type': 'OpeningHoursSpecification',
            closes: closeTime instanceof Date ?
              `${String(closeTime.getHours()).padStart(2, '0')}:${String(closeTime.getMinutes()).padStart(2, '0')}` :
              closeTime,
            dayOfWeek: day.day.charAt(0).toUpperCase() + day.day.slice(1),
            opens: openTime instanceof Date ?
              `${String(openTime.getHours()).padStart(2, '0')}:${String(openTime.getMinutes()).padStart(2, '0')}` :
              openTime
          };
        })
    })
  };

  return (
    <>
      {!!restaurantThemeColor && (
        // Restaurant color must be added inline in <head> to prevent
        // blinking of the original color when the page is loaded
        <NextHead>
          <style>
            {`
              :root {
                --restaurant-color: ${restaurantThemeColor};
              }
            `}
          </style>
        </NextHead>
      )}
      <div className={styles.restaurantMenu}>
        <div className={styles.restaurantMenu__contentWrapper}>
          <div className={styles.restaurantMenu__content}>
            <Head
              canonicalUrl={canonicalUrl}
              description={metaDescription}
              favicon={faviconUrl}
              isCdnLink
              keywords={metaKeywords}
              shareImage={restaurantData.coverPhotoUrl || restaurantData.logoUrl}
              structuredData={restaurantStructuredData}
              title={`${restaurantData.name} - ${restaurantData.category} Menu | Order Online`}
            />
            <Header
              className={styles.restaurantMenu__section}
              displayLocaleSelector
              restaurantInfo={{
                ...restaurantData,
                isOpen: restaurantIsOpen
              }}
              selectedLocale={selectedLocale}
              translations={{
                closed: pageTranslations.restaurantScheduleClosedTranslation,
                coverPhotoAlt: pageTranslations.restaurantCoverPhotoAltTranslation,
                information: pageTranslations.restaurantInfoTranslation,
                localeOptions: pageTranslations.localeOptions,
                logoAlt: pageTranslations.restaurantLogoAltTranslation,
                open: pageTranslations.restaurantScheduleOpenTranslation
              }}
              onMoreInfoButtonClick={handleMoreInfoButtonClick}
              onSelectedLocaleChange={setSelectedLocale}
            />
            <div
              ref={menuSectionsAndSearch}
              className={`${styles.restaurantMenu__menuSectionsAndSearch}${
                menuSectionsMenuAndSearchIsSticky ? ` ${styles.restaurantMenu__menuSectionsAndSearch_isSticky}` : ''}`}
            >
              <HorizontalMenu
                activeItem={selectedMenuSectionId}
                className={styles.restaurantMenu__menuSectionsMenu}
                items={menuSections}
                onItemClick={handleSectionAnchorClick}
              />
              <div className={styles.restaurantMenu__searchToolsContainer}>
                <SearchInput
                  className={styles.restaurantMenu__searchInput}
                  placeholder={pageTranslations.search.inputPlaceholder}
                  onChange={handleSearchInputChange}
                />
                <div className={styles.restaurantMenu__filtersToggleWrapper}>
                  <Button
                    className={`${styles.restaurantMenu__filtersToggle}${filtersAreVisible ? ` ${styles.restaurantMenu__filtersToggle_active}` : ''}`}
                    icon="funnel-simple"
                    theme="grey"
                    onClick={toggleFilters}
                  />
                  {!!filtersAppliedAmount && (
                    <Badge
                      className={styles.restaurantMenu__filtersToggleBadge}
                      text={filtersAppliedAmount}
                    />
                  )}
                </div>
                {orderingIsEnabled && (
                  <div className={styles.restaurantMenu__filtersToggleWrapper}>
                    <Button
                      className={`${styles.restaurantMenu__filtersToggle}${cartIsVisible ? ` ${styles.restaurantMenu__filtersToggle_active}` : ''}`}
                      icon="basket"
                      theme="grey"
                      onClick={toggleCart}
                    />
                    {!!cartProductsQuantity && (
                      <Badge
                        className={styles.restaurantMenu__filtersToggleBadge}
                        text={cartProductsQuantity}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            {Array.isArray(menu.publicMenuSections) &&
              (
                <ProductsListing
                  allowAddToCart={orderingIsEnabled}
                  className={`${styles.restaurantMenu__section} ${styles.restaurantMenu__productsListing}`}
                  menuSections={menu.publicMenuSections}
                  reduceGrid={reduceProductsGrid}
                  restaurantCurrency={restaurantData.currency}
                  searchString={searchInputValue}
                  selectedAllergens={selectedAllergens}
                  selectedLocale={selectedLocale}
                  selectedMaximumPrice={selectedPriceRange?.maximum}
                  selectedMinimumPrice={selectedPriceRange?.minimum}
                  selectedProductType={selectedProductType}
                  translations={pageTranslations}
                  onCurrentScrollSectionIdChange={handleCurrentScrollSectionIdChange}
                  onFilteredMenuSectionsUpdate={handleFilteredMenuSectionsUpdate}
                  onProductAddToCart={handleProductAddToCart}
                />
              )}
            <RestaurantMenuInfoModal
              restaurantInfo={{
                ...restaurantData,
                isOpen: restaurantIsOpen
              }}
              show={showMoreInfoModal}
              translations={{
                closed: pageTranslations.restaurantScheduleClosedTranslation,
                information: pageTranslations.restaurantInfoModalInformationTranslation,
                logoAlt: pageTranslations.restaurantLogoAltTranslation,
                open: pageTranslations.restaurantScheduleOpenTranslation,
                scheduleSeeLess: pageTranslations.restaurantInfoModalScheduleSeeLessTranslation,
                scheduleSeeMore: pageTranslations.restaurantInfoModalScheduleSeeMoreTranslation,
                socialMedia: pageTranslations.restaurantInfoModalSocialMediaTranslation,
                weekdays: pageTranslations.weekdays
              }}
              weekSchedule={weekSchedule}
              onClose={handleMoreInfoModalClose}
            />
            <footer className={styles.restaurantMenu__footer}>
              <div className={styles.restaurantMenu__footerFootnotes}>
                {COUNTRIES_WITH_VAT_DISCLAIMER.includes(restaurantData.country) && (
                  <span>
                    {pageTranslations.footnote}
                  </span>
                )}
                {!!restaurantData.footnote && (
                  <span>
                    {restaurantData.footnote}
                  </span>
                )}
              </div>
              <span>
                Powered by
                {' '}
                {/* We need this because we want to send the referrer header */}
                { }
                <a
                  href={poweredByLink}
                  target="_blank"
                >
                  Resty
                </a>
                { }
              </span>
            </footer>
          </div>
          <div className={`${styles.restaurantMenu__filtersWrapper}${filtersAreVisible || cartIsVisible ? ` ${styles.restaurantMenu__filtersWrapper_open}` : ''}`}>
            {(!orderingIsEnabled || lastActiveSidebarMenu === SIDEBAR_MENU_OPTIONS.FILTERS) ?
              (
                <RestaurantMenuFiltersSidebar
                  className={styles.restaurantMenu__filters}
                  productsAllergens={restaurantData.menu.productsAllergens}
                  productsMaximumPrice={restaurantData.menu.productsHighestPrice}
                  productsMinimumPrice={restaurantData.menu.productsLowestPrice}
                  productTypes={restaurantData.menu.productTypes}
                  restaurantCurrency={restaurantData.currency}
                  translations={pageTranslations.filters}
                  onAllergensFilterUpdate={handleAllergensFilterUpdate}
                  onClose={() => setFiltersAreVisible(false)}
                  onPriceIntervalFilterUpdate={handlePriceIntervalFilterUpdate}
                  onProductTypeFilterUpdate={handleProductTypeFilterUpdate}
                />

              ) :
              (
                <RestaurantMenuCartSidebar
                  allergensTranslations={pageTranslations.filters?.allergens}
                  className={styles.restaurantMenu__filters}
                  products={cartProducts}
                  selectedLocale={selectedLocale}
                  translations={pageTranslations.cart}
                  onCheckout={handleCartCheckout}
                  onClose={() => setCartIsVisible(false)}
                  onProductUpdate={handleCartProductUpdate}
                />
              )}
          </div>
        </div>
      </div>
      <RestaurantMenuOrderConfirmation
        allergensTranslations={pageTranslations.filters?.allergens}
        closedDays={closedDays}
        isRegisteringOrder={isRegisteringOrder}
        ordersClosingOffsetMinutes={ordersClosingOffsetMinutes}
        orderSuccess={orderSuccess}
        products={orderSuccess ? confirmedOrderProducts : cartProducts}
        restaurantAddresses={normalizedRestaurantAddresses}
        restaurantCountry={restaurantData?.country}
        restaurantName={restaurantData?.name}
        schedule={schedule}
        selectedLocale={selectedLocale}
        show={showOrderConfirmationModal}
        trackOrderUrl={trackOrderUrl}
        translations={pageTranslations.orderConfirmation}
        onClose={() => {
          setShowOrderConfirmationModal(false);
          setOrderSuccess(false);
          setConfirmedOrderId(null);
          setConfirmedOrderProducts([]);
        }}
        onConfirm={handleOrderConfirmation}
      />
    </>
  );
};

RestaurantMenu.propTypes = {
  canonicalUrl: PropTypes.string,
  pageTranslationsByLocale: PropTypes.shape({}).isRequired,
  restaurantData: PropTypes.shape({
    address: PropTypes.string,
    addresses: PropTypes.arrayOf(PropTypes.shape({
      address: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string
    })),
    category: PropTypes.string,
    color: PropTypes.string,
    country: PropTypes.string,
    coverPhotoUrl: PropTypes.string,
    currency: PropTypes.string,
    footnote: PropTypes.string,
    logoUrl: PropTypes.string,
    menu: PropTypes.shape({
      productTypes: PropTypes.arrayOf(PropTypes.string),
      productsAllergens: PropTypes.arrayOf(PropTypes.string),
      productsHighestPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      productsLowestPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      publicMenuSections: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    name: PropTypes.string,
    ordersClosingOffsetMinutes: PropTypes.number,
    ordersEnabled: PropTypes.bool,
    ordersPausedUntil: PropTypes.string,
    schedule: PropTypes.arrayOf(PropTypes.shape({})),
    scheduleClosedEvents: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

;

export default RestaurantMenu;


    export async function getServerSideProps(ctx) {
        let res = _getServerSideProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/restaurant/[slug]',
              loaderName: 'getServerSideProps',
              ...__i18nConfig,
              
            }))
          }
        }
    }
  